import React from "react";
import { Folder, Loader, MessageSquare, RotateCcw } from "lucide-react";
import {
  RecentlyDeleted,
  RecentlyDeletedProject,
  RecentlyDeletedSession,
} from "../../service/apiService";
import {
  canStillRestore,
  describeRetention,
  formatWindowTime,
  timeLeftToRestore,
} from "../../utils/deletionWindow";

/**
 * The "Recently deleted" view of Chat History.
 *
 * Projects and conversations the viewer deleted (and, for a super admin,
 * what another super admin deleted) that are still inside the deletion
 * window. Rows are not clickable and have no menu: Restore is the only
 * action. Conversations deleted together with a project are listed under
 * that project and come back with it.
 */
interface RecentlyDeletedListProps {
  items: RecentlyDeleted | null;
  loading: boolean;
  error: boolean;
  restoringId: string | null;
  searchTerm: string;
  /** "Deleted by" and "owner" are shown only when they are someone else. */
  currentUserId?: string;
  onRestoreProject: (project: RecentlyDeletedProject) => void;
  onRestoreSession: (session: RecentlyDeletedSession) => void;
}

const isSomeoneElse = (email?: string, me?: string) =>
  !!email && (!me || email.toLowerCase() !== me.toLowerCase());

const PREVIEW_COUNT = 3;

const RestoreButton: React.FC<{ busy: boolean; onClick: () => void }> = ({ busy, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-violet-700 border border-gray-200 rounded-lg hover:bg-violet-50 disabled:opacity-50"
  >
    {busy ? <Loader size={13} className="animate-spin" /> : <RotateCcw size={13} />}
    Restore
  </button>
);

const Meta: React.FC<{
  deletedBy?: string;
  deletedAt?: string;
  purgeAt?: string;
  currentUserId?: string;
}> = ({ deletedBy, deletedAt, purgeAt, currentUserId }) => (
  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
    {deletedAt && (
      <span>
        Deleted
        {isSomeoneElse(deletedBy, currentUserId) && ` by ${deletedBy}`}
        {` · ${formatWindowTime(deletedAt)}`}
      </span>
    )}
    <span className="text-amber-700 font-medium">{timeLeftToRestore(purgeAt)}</span>
  </div>
);

const RecentlyDeletedList: React.FC<RecentlyDeletedListProps> = ({
  items,
  loading,
  error,
  restoringId,
  searchTerm,
  currentUserId,
  onRestoreProject,
  onRestoreSession,
}) => {
  if (loading && !items) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader className="animate-spin" size={24} />
      </div>
    );
  }
  if (error && !items) {
    return (
      <div className="text-center text-gray-500 py-8">
        <p className="text-sm">Could not load recently deleted items.</p>
      </div>
    );
  }

  const term = searchTerm.trim().toLowerCase();
  const matches = (title: string) => !term || title.toLowerCase().includes(term);
  const projects = (items?.projects || []).filter(
    (p) => matches(p.project_name) || p.sessions.some((s) => matches(s.title))
  );
  const sessions = (items?.sessions || []).filter((s) => matches(s.title));
  const retention = describeRetention(items?.retention_hours);

  if (projects.length === 0 && sessions.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <p className="text-sm">
          {term ? "No recently deleted items match your search." : `Nothing deleted in the last ${retention}.`}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 px-1 pb-1">
        Deleted in the last {retention}. Restore anything you still need. After{" "}
        {retention} it is gone for good.
      </p>

      {projects.map((project) => {
        const preview = project.sessions.slice(0, PREVIEW_COUNT);
        const more = project.sessions.length - preview.length;
        const brainBanks = project.deletion_options?.delete_brain_banks;
        // Without a cascade only the project went; its conversations stayed
        // in History, so the row does not count them as deleted.
        const withConversations = project.deletion_options?.cascade === true;
        return (
          <div
            key={`project-${project.id}`}
            className="text-sm p-3 rounded-lg border border-gray-100 text-gray-500 flex justify-between items-start gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 font-medium text-gray-700">
                <Folder size={14} className="flex-shrink-0" />
                <span className="truncate">{project.project_name}</span>
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Project
                {withConversations &&
                  ` · ${project.conversation_count} ${project.conversation_count === 1 ? "conversation" : "conversations"}`}
                {brainBanks && " · BrainBanks included"}
              </div>
              <Meta
                deletedBy={project.deleted_by}
                deletedAt={project.deleted_at}
                purgeAt={project.purge_at}
                currentUserId={currentUserId}
              />
              {preview.length > 0 && (
                <ul className="mt-2 pl-5 space-y-0.5 text-xs text-gray-500">
                  {preview.map((s) => (
                    <li key={s.id} className="truncate">
                      {s.title || "Untitled conversation"}
                    </li>
                  ))}
                  {more > 0 && <li>+ {more} more</li>}
                </ul>
              )}
            </div>
            {canStillRestore(project.purge_at) && (
              <RestoreButton
                busy={restoringId === project.id}
                onClick={() => onRestoreProject(project)}
              />
            )}
          </div>
        );
      })}

      {sessions.map((session) => (
        <div
          key={`session-${session.id}`}
          className="text-sm p-3 rounded-lg border border-gray-100 text-gray-500 flex justify-between items-start gap-3"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 font-medium text-gray-700">
              <MessageSquare size={14} className="flex-shrink-0" />
              <span className="truncate">{session.title || "Untitled conversation"}</span>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              Conversation
              {isSomeoneElse(session.user_id, currentUserId) && ` · owner ${session.user_id}`}
            </div>
            <Meta
              deletedBy={session.deleted_by}
              deletedAt={session.deleted_at}
              purgeAt={session.purge_at}
              currentUserId={currentUserId}
            />
          </div>
          {canStillRestore(session.purge_at) && (
            <RestoreButton
              busy={restoringId === session.id}
              onClick={() => onRestoreSession(session)}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default RecentlyDeletedList;
